/**
 * EditableSlotGroup Component
 * 
 * Wrapper component that makes a slot group (compound token with multiple fields)
 * selectable and editable in edit mode.
 * 
 * Features:
 * - Shows hover outline when edit mode is active
 * - Click opens inline slot group editor (if enabled) or selects for sidebar
 * - Displays slot group label on hover
 */

import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
  type CSSProperties,
} from 'react';

import { useEditMode, requestInlineSlotGroupEditor, postScopeTargetSelected, postCommentPinTargetSelected, type TokenBounds } from './edit-mode';
import { useEditableRegion, EditableRegionHoverLabel, editableRegionStyles } from './editable-region';
import { useGeneratingContent } from './generating-paths';
import { useScopedStyleOverride, useVibesites, buildScopeContext } from './context';
import { resolveScopedOverride } from '../scope';
import type { SlotGroupDefinition, SlotGroupOverride } from '../types';
import { useSectionEditInfo } from './editable-section';
import { useReportedRuntimeDefinition } from './runtime-definitions';

// ============================================================================
// Types
// ============================================================================

export interface EditableSlotGroupProps {
  /** Slot group path (e.g., "imagegridsection.image_1") */
  groupPath: string;
  /** Slot group definition */
  definition: SlotGroupDefinition;
  /** Current override (if any) */
  override?: SlotGroupOverride | null;
  /** Save callback for slot group override */
  onSave?: (override: SlotGroupOverride | null) => Promise<void>;
  /** Children to render (the slot group content) */
  children: ReactNode;
  /** Additional class name */
  className?: string;
  /** Additional inline styles */
  style?: CSSProperties;
}

// ============================================================================
// Component
// ============================================================================

export function EditableSlotGroup({
  groupPath,
  definition,
  override,
  children,
  className = '',
  style,
}: EditableSlotGroupProps) {
  const editMode = useEditMode();
  const sectionEditInfo = useSectionEditInfo();
  const { slotGroupOverrides, page } = useVibesites();
  const [isEditing, setIsEditing] = useState(false);
  const styleOverride = useScopedStyleOverride(groupPath);

  // Get edit mode state
  const isEditMode = editMode?.isEditMode ?? false;

  useReportedRuntimeDefinition('slotGroup', groupPath, definition, isEditMode);

  // The scoped slot-group override that actually backs `children` (themes often
  // don't pass the `override` prop). Memoized + editor-gated: off the editor it's
  // null with no resolve work; in the editor it's reused by the click handler.
  const effectiveOverride = useMemo(
    () =>
      editMode
        ? resolveScopedOverride(slotGroupOverrides, groupPath, buildScopeContext(page)) ?? override ?? null
        : null,
    [editMode, slotGroupOverrides, groupPath, page, override],
  );
  // Generating SHIMMER (editor-preview only): wraps `children` while this group
  // is actively generating. Off the editor it's a pure pass-through.
  const content = useGeneratingContent(groupPath, children, 'div');

  const handleSelect = useCallback((region: HTMLElement) => {
    if (!isEditMode || !editMode) return;

    const rect = region.getBoundingClientRect();

    const bounds: TokenBounds = {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      bottom: rect.bottom,
      right: rect.right,
    };

    // Scope mode routes the click to the parent's AI composer instead of
    // opening the inline slot-group editor.
    if (editMode.scopeMode) {
      postScopeTargetSelected({
        kind: 'slotGroup',
        componentInfo: sectionEditInfo,
        groupPath,
      });
      return;
    }
    if (editMode.commentPinMode) {
      postCommentPinTargetSelected({
        kind: 'slotGroup',
        componentInfo: sectionEditInfo,
        groupPath,
      });
      return;
    }

    setIsEditing(true);
    requestInlineSlotGroupEditor({
      groupPath,
      definition: definition as unknown as Record<string, unknown>,
      override: effectiveOverride,
      anchorBounds: bounds,
      componentInfo: sectionEditInfo,
    });
  }, [isEditMode, editMode, groupPath, definition, effectiveOverride, sectionEditInfo]);

  const { regionRef: groupRef, isHovered, regionProps } = useEditableRegion({
    enabled: isEditMode,
    nestedEditableSelector: '[data-vibesite-token]',
    onSelect: handleSelect,
  });

  // Listen for close signal from parent editor overlay
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'VIBESITE_INLINE_EDIT_CLOSED' && event.data?.groupPath === groupPath) {
        setIsEditing(false);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [groupPath]);

  // If not in edit mode, render with wrapper to preserve styles
  if (!editMode || !isEditMode) {
    return (
      <div className={className} style={{ ...style, ...styleOverride?.properties }}>
        {content}
      </div>
    );
  }

  const rect = isHovered && !isEditing ? groupRef.current?.getBoundingClientRect() : null;

  return (
    <>
      <div
        ref={groupRef}
        className={`vibesite-editable-region vibesite-editable-slot-group ${className}`}
        data-vibesite-slot-group={groupPath}
        data-vibesite-region-hovered={isHovered && !isEditing}
        data-vibesite-slot-group-editing={isEditing}
        {...regionProps}
        style={{
          cursor: 'pointer',
          ...style,
          ...styleOverride?.properties,
        }}
      >
        {content}
        <style>{editableRegionStyles + editableSlotGroupStyles}</style>
      </div>

      {/* Portaled hover label — rendered outside to avoid position/style interference */}
      {isHovered && !isEditing && rect && (
        <EditableRegionHoverLabel
          rect={rect}
          style={{
            marginTop: -8,
            background: '#773BFF',
            fontSize: 10,
            fontFamily: 'ui-sans-serif, system-ui, sans-serif',
            fontWeight: 500,
            padding: '4px 10px',
            zIndex: 10001,
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
          {definition.label}
        </EditableRegionHoverLabel>
      )}
    </>
  );
}

const editableSlotGroupStyles = `
/* Editing state */
.vibesite-editable-slot-group[data-vibesite-slot-group-editing="true"] {
  outline: 2px solid #773BFF !important;
  outline-offset: 4px !important;
}
`;

export default EditableSlotGroup;
